import ForegroundNotificationBanner from "./components/member/ForegroundNotificationBanner";
import {installPushAccountBoundary} from "./services/announcementPushService";
import CustomDomainAuthBoundary from "./components/brand/CustomDomainAuthBoundary";
import {PersistentAdminLayout} from "./components/layout/PersistentAdminLayout";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import {PublicOrganizationProvider} from "./hooks/usePublicOrganization";
import {AttendanceVerificationProvider} from "./hooks/useAttendanceAuthorizationFreshness";
// src/App.js
import React, { Suspense } from "react";
import { retryableLazy as lazy } from "./utils/retryableLazy";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";

/* ---------------- Lazy route pages ---------------- */
import * as pageLoaders from "./routes/pageLoaders";
import RouteLoadingFallback from "./components/loading/PremiumSkeletons";
import NavigationWarmup from "./components/navigation/NavigationWarmup";
import IdentityLinkBootstrap from "./components/IdentityLinkBootstrap";
import LegacyAuthRedirect from "./pages/LegacyAuthRedirect";
import OrganizationNotFound from "./pages/OrganizationNotFound";

import RequireAuth from "./components/RequireAuth";
import RequireOrgAccess from "./components/RequireOrgAccess";
import { PermissionsProvider } from "./hooks/usePermissions";
import { redirectLegacyRccgHost } from "./utils/organizationAliases";
import { buildLegacyDashboardRedirect } from "./utils/routing/legacyDashboardRedirect";
import {
  BUILDING_FUND_DONATION_ROUTE_SLUG,
  buildCanonicalPublicPath,
  resolveCanonicalPublicRoute,
} from "./utils/canonicalPublicRoutes";
import { redirectWwwToPrimary } from "./config/domains";
import { getCurrentHostname, shouldUseCustomDomainPublicRoutes } from "./utils/routing/hostUtils";
import { getAttendanceRouteMode, getChildrenCheckInRouteMode } from "./utils/attendanceRouteMode";

// Custom-domain `/profile` -> `/me`. See the component for the scope note: the
// public `/profile` pages in the other two route trees are untouched.
import CustomDomainMemberProfileRedirect from "./routes/CustomDomainMemberProfileRedirect";
const WebsitePreviewPage = lazy(() => import('./pages/WebsitePreviewPage'));
// Lightly Events: personal and organization events (separate from org event registration).
const EventsHomePage = lazy(() => import("./features/lightlyEvents/EventsHomePage"));
const EventEditorPage = lazy(() => import("./features/lightlyEvents/EventEditorPage"));
const EventManagePage = lazy(() => import("./features/lightlyEvents/EventManagePage"));
const EventCheckInPage = lazy(() => import("./features/lightlyEvents/EventCheckInPage"));
const EventPublicPage = lazy(() => import("./features/lightlyEvents/EventPublicPage"));
const EventsOperatorPage = lazy(() => import("./features/lightlyEvents/EventsOperatorPage"));

/* First-open entry budget: these routes are never part of a public church
 * page, and together with framer-motion/react-icons they made up a large
 * share of main.js. They load on demand inside the app Suspense boundary. */
const AuthActionHandler = lazy(() => import("./pages/AuthActionHandler"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const OrganizationSearchBox = lazy(() => import("./components/OrganizationSearchBox"));
const RequireCuisineOrganization = lazy(() => import("./components/RequireCuisineOrganization"));

/* ---------------- Onboarding ---------------- */
const OnboardingSlide1 = lazy(() => import("./pages/OnboardingSlide1"));
const OnboardingSlide2 = lazy(() => import("./pages/OnboardingSlide2"));
const OnboardingSlide3 = lazy(() => import("./pages/OnboardingSlide3"));
const OnboardingSlide4 = lazy(() => import("./pages/OnboardingSlide4"));
const OnboardingSlide5 = lazy(() => import("./pages/OnboardingSlide5"));
const OnboardingSlide6 = lazy(() => import("./pages/OnboardingSlide6"));
const OnboardingTestLab = lazy(() => import("./pages/OnboardingTestLab"));
const OrganizationChoice = lazy(() => import("./pages/organizationchoice"));
const MissionHookStep1 = lazy(() => import("./pages/MissionHookStep1"));
const Organizationpath = lazy(() => import("./pages/organizationpath"));
const Organizationpath2 = lazy(() => import("./pages/organizationpath2"));
const Individualpath = lazy(() => import("./pages/individualpath"));
const Individualpath2 = lazy(() => import("./pages/individualpath2"));
const Individualpath3 = lazy(() => import("./pages/individualpath3"));

const ParticipantPage = lazy(pageLoaders.loadParticipantPage);
const ParticipantSignupPage = lazy(pageLoaders.loadParticipantSignupPage);
const OrgCheckInPage = lazy(pageLoaders.loadOrgCheckInPage);
const AttendanceDashboardPage = lazy(pageLoaders.loadAttendanceDashboardPage);
const MemberAttendancePage = lazy(pageLoaders.loadMemberAttendancePage);
const ChildrenCheckInPage = lazy(pageLoaders.loadChildrenCheckInPage);
const ChildrenInvitationPage = lazy(() => import("./pages/ChildrenInvitationPage"));
const MemberInvitationPage = lazy(() => import("./pages/MemberInvitationPage"));
const ChildrenTeachersPage = lazy(pageLoaders.loadChildrenTeachersPage);
const ChildrenClassesAdminPage = lazy(pageLoaders.loadChildrenClassesAdminPage);
const ChildrenCheckoutPage = lazy(pageLoaders.loadChildrenCheckoutPage);
const MyGivingPage = lazy(pageLoaders.loadMyGivingPage);
const DonatePage = lazy(pageLoaders.loadDonatePage);
const PublicCampaignPage = lazy(pageLoaders.loadPublicCampaignPage);
const CampaignAdminPage = lazy(pageLoaders.loadCampaignAdminPage);
const RequireCampaignEditAccess = lazy(() => import("./components/RequireCampaignEditAccess"));
const CampaignCreatePageNew = lazy(pageLoaders.loadCampaignCreatePage);
const CampaignLaunchPage = lazy(pageLoaders.loadCampaignLaunchPage);
const CampaignProposalPage = lazy(pageLoaders.loadCampaignProposalPage);
const CampaignProposalReviewPage = lazy(pageLoaders.loadCampaignProposalReviewPage);
const CampaignParticipantSignupPage = lazy(pageLoaders.loadCampaignParticipantSignupPage);
const PlatformTaxReviewPage = lazy(() => import("./pages/PlatformTaxReviewPage"));
const PlatformGovernancePage = lazy(() => import("./pages/PlatformGovernancePage"));
const PlatformHome = lazy(pageLoaders.loadPlatformHome);
const OrgDashboard = lazy(pageLoaders.loadOrgDashboard);
const OrgProfilePage = lazy(pageLoaders.loadOrgProfilePage);
const OrgGroupsManagerPage = lazy(pageLoaders.loadOrgGroupsManagerPage);
const OrgPublicProfile = lazy(pageLoaders.loadOrgPublicProfile);
const MemberProfilePage = lazy(pageLoaders.loadMemberProfilePage);
// NOTE: the second member-profile implementation (MemberProfileRoute /
// MemberProfileScreen) is no longer routed — `/me` is the single canonical
// member profile and custom-domain `/profile` redirects to it via
// CustomDomainMemberProfileRedirect (imported at the top of this module). The
// components remain in the tree (still unit-tested, and their giving source now
// goes through the governed pipeline) pending the retirement pass, but they
// must not be re-registered as a route without first closing the payload gaps
// documented in docs/engineering/CANONICAL_MEMBER_PROFILE.md.
const CareCenterPage = lazy(pageLoaders.loadCareCenterPage);
const MyFollowUpsPage = lazy(pageLoaders.loadMyFollowUpsPage);
const OrgVerificationPage = lazy(pageLoaders.loadOrgVerificationPage);
const OrgPayoutSetupPage = lazy(pageLoaders.loadOrgPayoutSetupPage);
const TeamRolesPage = lazy(pageLoaders.loadTeamRolesPage);
const DesignCenterPage = lazy(pageLoaders.loadDesignCenterPage);
const AutomationCatalogPage = lazy(pageLoaders.loadAutomationCatalogPage);
const NotificationCenterPage = lazy(pageLoaders.loadNotificationCenterPage);
const AdaptiveExperienceLabPage = lazy(pageLoaders.loadAdaptiveExperienceLabPage);
const FundingCenterPage = lazy(pageLoaders.loadFundingCenterPage);
const OpportunityTasksPage = lazy(pageLoaders.loadOpportunityTasksPage);
const DonorsPage = lazy(pageLoaders.loadDonorsPage);
const ReportsPage = lazy(pageLoaders.loadReportsPage);
const WebsiteVisitorsPage = lazy(pageLoaders.loadWebsiteVisitorsPage);
const MediaVaultPage = lazy(pageLoaders.loadMediaVaultPage);
const AdminOrganizationTeamsPage = lazy(pageLoaders.loadAdminOrganizationTeamsPage);
const MyTeamsPage = lazy(pageLoaders.loadMyTeamsPage);
const OrganizationTeamWorkspacePage = lazy(pageLoaders.loadOrganizationTeamWorkspacePage);
const MemberServiceProgramsPage = lazy(() => import('./pages/MemberServiceProgramsPage'));
const MemberMeetingsPage = lazy(() => import("./pages/MemberMeetingsPage"));
const MemberServicesManagementPage = lazy(() => import('./pages/MemberServicesManagementPage'));
const MemberDirectoryPage = lazy(() => import('./pages/MemberDirectoryPage'));
const FamilyHistoryPage = lazy(() => import('./pages/FamilyHistoryPage'));
const MemberEventRegistrationsPage = lazy(() => import('./pages/MemberEventRegistrationsPage'));
const MemberPledgesPage = lazy(() => import('./pages/MemberPledgesPage'));
const MemberSettingsPage = lazy(() => import("./pages/MemberSettingsPage"));
const MemberNotificationsPage = lazy(() => import("./pages/MemberNotificationsPage"));
const MemberCarePage = lazy(() => import("./pages/MemberCarePage"));
const LearningResourcesPage = lazy(() => import('./pages/organizationNetwork/LearningResourcesPage'));
const AnnouncementsInboxPage = lazy(pageLoaders.loadAnnouncementsInboxPage);
const PublicAnnouncementSharePage = lazy(pageLoaders.loadPublicAnnouncementSharePage);
const GalleryPage = lazy(pageLoaders.loadGalleryPage);
const PublicMinistryPage = lazy(pageLoaders.loadPublicMinistryPage);
const PublicChurchRoutePage = lazy(pageLoaders.loadPublicChurchRoutePage);
const PublicOrganizationWebsite = lazy(pageLoaders.loadPublicOrganizationWebsite);
const CustomDomainOrganizationWebsite = lazy(pageLoaders.loadCustomDomainOrganizationWebsite);
const LightlyPlatformHome = lazy(pageLoaders.loadLightlyPlatformHome);
const ChooseOrganizationForDonation = lazy(pageLoaders.loadChooseOrganizationForDonation);
const PlatformMemberContextPage = lazy(() => import("./pages/PlatformMemberContextPage"));
const DonationStatusPage = lazy(pageLoaders.loadDonationStatusPage);
const FundraiserLaunchPage = lazy(pageLoaders.loadFundraiserLaunchPage);
const OrganizationLaunchExperiencePage = lazy(pageLoaders.loadOrganizationLaunchExperiencePage);
const IndividualMissionDiscoveryPage = lazy(pageLoaders.loadIndividualMissionDiscoveryPage);
const GroupsPage = lazy(pageLoaders.loadGroupsPage);
const AdminDashboard = lazy(pageLoaders.loadAdminDashboard);
const OrgOnboardingWizard = lazy(pageLoaders.loadOrgOnboardingWizard);
const PublicOrgDashboardPage = lazy(pageLoaders.loadPublicOrgDashboardPage);
const OrganizationManagementEntry = lazy(() => import('./pages/OrganizationManagementEntry'));
const CuisineCatalogEditorPage = lazy(pageLoaders.loadCuisineCatalogEditorPage);
const PublicCuisineStorefrontPage = lazy(pageLoaders.loadPublicCuisineStorefrontPage);
const CuisineMenuPage = lazy(() => import('./pages/cuisine/CuisineMenuPage'));
const CuisineCateringRequestPage = lazy(pageLoaders.loadCuisineCateringRequestPage);
const CuisineRequestInboxPage = lazy(pageLoaders.loadCuisineRequestInboxPage);
const CuisinePolicyPage = lazy(pageLoaders.loadCuisinePolicyPage);
const CuisineQuotePage = lazy(pageLoaders.loadCuisineQuotePage);
const CuisineBookingDiaryPage = lazy(pageLoaders.loadCuisineBookingDiaryPage);
const CuisineStudioPage = lazy(pageLoaders.loadCuisineStudioPage);
const PublicLeadershipPage = lazy(pageLoaders.loadPublicLeadershipPage);
const OrgBiblePage = lazy(pageLoaders.loadOrgBiblePage);
const OrgMorePage = lazy(pageLoaders.loadOrgMorePage);
const ServiceManager = lazy(pageLoaders.loadServiceManager);
const ServiceContentAdminPage = lazy(pageLoaders.loadServiceContentAdminPage);
const PublicServiceHub = lazy(pageLoaders.loadPublicServiceHub);
const PublicServiceContentLibrary = lazy(pageLoaders.loadPublicServiceContentLibrary);
const DaycareOverviewPage = lazy(pageLoaders.loadDaycareOverviewPage);
const DaycareStaffAssignmentsPage = lazy(pageLoaders.loadDaycareStaffAssignmentsPage);
const DaycareEnrollmentApplyPage = lazy(pageLoaders.loadDaycareEnrollmentApplyPage);
const DaycareEnrollmentQueuePage = lazy(pageLoaders.loadDaycareEnrollmentQueuePage);
const DaycareRelationshipsPage = lazy(pageLoaders.loadDaycareRelationshipsPage);
const DaycareParentPortalPage = lazy(pageLoaders.loadDaycareParentPortalPage);
const DaycareTuitionPage = lazy(pageLoaders.loadDaycareTuitionPage);
const DaycareClassroomsPage = lazy(pageLoaders.loadDaycareClassroomsPage);
const DaycareAttendancePage = lazy(pageLoaders.loadDaycareAttendancePage);
const DaycareCarePage = lazy(pageLoaders.loadDaycareCarePage);
const PublicDaycarePage = lazy(pageLoaders.loadPublicDaycarePage);
const PhoneRegistrationPage = lazy(() => import("./pages/PhoneRegistrationPage"));
const LoginPage = lazy(() => import("./LegacyAppFlow"));

function LegacyNestedOrganizationRedirect() {
  const { orgSlug } = useParams();
  const location = useLocation();
  const legacyPrefix = `/${String(location.pathname || "")
    .replace(/^\/+/, "")
    .split("/")
    .slice(0, 3)
    .join("/")}`;
  const restPath = String(location.pathname || "").startsWith(legacyPrefix)
    ? String(location.pathname || "").slice(legacyPrefix.length)
    : "";
  const destination = `/${orgSlug || ""}${restPath || ""}${location.search || ""}${location.hash || ""}`;

  return <Navigate to={destination} replace />;
}

function AttendanceRouteGate() {
  const location = useLocation();
  const mode = getAttendanceRouteMode(location.search);

  if (mode === "operational") return <OrgCheckInPage />;
  if (mode === "member") return <RequireOrgMember><MemberAttendancePage /></RequireOrgMember>;
  return (
    <RequireOrgCapability permission="canManageAttendance">
      <AttendanceDashboardPage />
    </RequireOrgCapability>
  );
}

function ChildrenCheckInRouteGate() {
  const location = useLocation();
  // Children Check-In policy: service/session/token/kiosk links stay public so
  // guardians can scan a QR code and check in without a Lightly account.
  const mode = getChildrenCheckInRouteMode(location.search);

  return mode === "operational"
    ? <ChildrenCheckInPage autoResolveService />
    : (
      <RequireOrgCapability
        permission={["canManageChildrenCheckin", "canCheckInChildren", "canCheckOutChildren"]}
      >
        <ChildrenCheckInPage autoResolveService />
      </RequireOrgCapability>
    );
}

function PrivateOrgRedirect({ to }) {
  const { orgSlug = "" } = useParams();
  const location = useLocation();
  return <Navigate to={`/${orgSlug}${to}${location.search || ""}${location.hash || ""}`} replace />;
}

// Org-scoped route guards (defense-in-depth on top of Firestore rules).
// RequireOrgMember: any active member/admin/owner of the org in the route.
// RequireOrgAdmin: org owner or admin only.
// RequireOrgCapability: owner/admin, or an active member holding the capability.
function RequireOrgMember({ children }) {
  return <RequireOrgAccess require="member">{children}</RequireOrgAccess>;
}

/* Own profile, giving, registration and pledge records only. A non-member
 * reaches their OWN records and nothing else; member-only routes keep using
 * RequireOrgMember. */
function RequireVerifiedAccount({ children }) {
  return <RequireOrgAccess require="verifiedAccount">{children}</RequireOrgAccess>;
}

function RequireOrgAdmin({ children }) {
  return <RequireOrgAccess require="admin">{children}</RequireOrgAccess>;
}

function RequireOrgCapability({ permission, children }) {
  return <RequireOrgAccess require={permission}>{children}</RequireOrgAccess>;
}

function RequireOrgModule({ moduleId, children }) {
  return <RequireOrgAccess require={{ moduleId }}>{children}</RequireOrgAccess>;
}

function PlatformOrgRedirect({ to }) {
  const { orgSlug = "" } = useParams();
  const location = useLocation();
  return <Navigate to={`/platform/o/${orgSlug}${to}${location.search || ""}${location.hash || ""}`} replace />;
}

function LegacyPlatformDashboardRedirect() {
  const { orgSlug = "" } = useParams();
  const location = useLocation();
  return <Navigate to={buildLegacyDashboardRedirect(orgSlug, location)} replace />;
}

function CanonicalPublicRouteRedirect() {
  const params = useParams();
  const location = useLocation();
  const resolved = resolveCanonicalPublicRoute({
    pathname: location.pathname,
    params,
  });
  const destination = `${buildCanonicalPublicPath(resolved)}${location.search || ""}${location.hash || ""}`;
  return <Navigate to={destination} replace />;
}

function LegacyDonationRouteGate() {
  const { slug = "" } = useParams();
  if (slug === BUILDING_FUND_DONATION_ROUTE_SLUG) {
    return <CanonicalPublicRouteRedirect />;
  }
  return <DonatePage />;
}

function CustomDomainPublicRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<CustomDomainAuthBoundary><LoginPage /></CustomDomainAuthBoundary>} />
      <Route path="/verify-phone" element={<CustomDomainAuthBoundary><PhoneRegistrationPage /></CustomDomainAuthBoundary>} />
      <Route path="/connect-profile" element={<CustomDomainAuthBoundary><PhoneRegistrationPage /></CustomDomainAuthBoundary>} />
      <Route path="/register" element={<CustomDomainAuthBoundary register />} />
      <Route path="/children-invitation" element={<ChildrenInvitationPage />} />
      <Route path="/member-invitation" element={<MemberInvitationPage />} />
      <Route path="/auth/action" element={<CustomDomainAuthBoundary><AuthActionHandler /></CustomDomainAuthBoundary>} />
      <Route path="/verify-email" element={<CustomDomainAuthBoundary><LegacyAuthRedirect /></CustomDomainAuthBoundary>} />
      <Route path="/reset-password" element={<CustomDomainAuthBoundary><LegacyAuthRedirect /></CustomDomainAuthBoundary>} />
      <Route path="/action" element={<CustomDomainAuthBoundary><LegacyAuthRedirect /></CustomDomainAuthBoundary>} />
      <Route path="/" element={<CustomDomainOrganizationWebsite />}>
        <Route index element={<PublicOrgDashboardPage />} />
        <Route path="donate" element={<DonatePage />} />
        <Route path="donate/:donationRouteSlug" element={<DonatePage />} />
        <Route path="success" element={<DonationStatusPage status="success" />} />
        <Route path="cancel" element={<DonationStatusPage status="cancel" />} />
        <Route path="about" element={<PublicChurchRoutePage pageType="about" />} />
        <Route path="new-here" element={<PublicChurchRoutePage pageType="new-here" />} />
        <Route path="beliefs" element={<PublicChurchRoutePage pageType="beliefs" />} />
        <Route path="media" element={<PublicChurchRoutePage pageType="media" />} />
        <Route path="media/watch-live" element={<PublicChurchRoutePage pageType="watch-live" />} />
        <Route path="media/sermons" element={<PublicChurchRoutePage pageType="sermons" />} />
        <Route path="sermons" element={<PublicChurchRoutePage pageType="sermons" />} />
        <Route path="media/testimonies" element={<PublicChurchRoutePage pageType="testimonies" />} />
        <Route path="updates" element={<PublicChurchRoutePage pageType="updates" />} />
        <Route path="updates/:announcementId" element={<PublicAnnouncementSharePage />} />
        <Route path="flyers" element={<PublicChurchRoutePage pageType="flyers" />} />
        <Route path="messages" element={<PublicServiceContentLibrary />} />
        <Route path="messages/:contentId" element={<PublicServiceContentLibrary />} />
        <Route path="series/:seriesId" element={<PublicServiceContentLibrary />} />
        <Route path="events" element={<PublicChurchRoutePage pageType="events" />} />
        <Route path="events/:eventId" element={<PublicChurchRoutePage pageType="event-detail" />} />
        <Route path="services" element={<PublicServiceHub />} />
        <Route path="services/:serviceId" element={<PublicServiceHub />} />
        <Route path="menu" element={<PublicChurchRoutePage pageType="menu" />} />
        <Route path="give" element={<PublicChurchRoutePage pageType="giving" />} />
        <Route path="give/success" element={<DonationStatusPage status="success" />} />
        <Route path="giving" element={<PublicChurchRoutePage pageType="giving" />} />
        <Route path="campaigns" element={<PublicChurchRoutePage pageType="campaigns" />} />
        <Route path="campaigns/:campaignId/launch" element={<CampaignLaunchPage />} />
        <Route path="campaigns/:campaignId/success" element={<DonationStatusPage status="success" />} />
        <Route path="campaigns/:campaignId" element={<PublicCampaignPage />} />
        <Route path="campaigns/:campaignId/donate" element={<DonatePage />} />
        <Route path="campaigns/:campaignId/become-a-fundraiser" element={<CampaignParticipantSignupPage />} />
        <Route path="campaigns/:campaignId/fundraisers/:slug" element={<ParticipantPage />} />
        <Route path="campaigns/:campaignId/fundraisers/:slug/edit" element={<CampaignParticipantSignupPage />} />
        <Route path="campaigns/:campaignId/fundraisers/:slug/donate" element={<DonatePage />} />
        <Route path="campaigns/:campaignId/f/:slug" element={<ParticipantPage />} />
        <Route path="campaigns/:campaignId/f/:slug/edit" element={<CampaignParticipantSignupPage />} />
        <Route path="campaigns/:campaignId/f/:slug/donate" element={<DonatePage />} />
        <Route path="c/:campaignId/fundraisers/:fundraiserSlug/launch" element={<FundraiserLaunchPage />} />
        <Route path="fundraisers/:fundraiserId/success" element={<DonationStatusPage status="success" />} />
        <Route path="contact" element={<PublicChurchRoutePage pageType="contact" />} />
        <Route path="prayer-request" element={<PublicChurchRoutePage pageType="prayer" />} />
        <Route path="privacy" element={<PublicChurchRoutePage pageType="privacy" />} />
        <Route path="refund-policy" element={<PublicChurchRoutePage pageType="refund-policy" />} />
        <Route path="terms" element={<PublicChurchRoutePage pageType="terms" />} />
        <Route path="ministries" element={<PublicMinistryPage />} />
        <Route path="ministries/:ministrySlug" element={<PublicMinistryPage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="leadership" element={<PublicLeadershipPage />} />
        <Route path="about/leadership" element={<PublicLeadershipPage />} />
        <Route path="more" element={<OrgMorePage />} />
        <Route path="bible" element={<OrgBiblePage />} />
        <Route path="me" element={<RequireVerifiedAccount><MemberProfilePage /></RequireVerifiedAccount>} />
        <Route path="profile" element={<CustomDomainMemberProfileRedirect />} />
        <Route path="my-giving" element={<RequireVerifiedAccount><MyGivingPage /></RequireVerifiedAccount>} />
<Route path="settings" element={<RequireVerifiedAccount><MemberSettingsPage /></RequireVerifiedAccount>} />
        <Route path="follow-ups" element={<RequireOrgMember><MyFollowUpsPage /></RequireOrgMember>} />
        <Route path="announcements" element={<RequireOrgMember><AnnouncementsInboxPage /></RequireOrgMember>} />
        <Route path="app/teams" element={<RequireOrgMember><MyTeamsPage /></RequireOrgMember>} />
        <Route path="app/service-programs" element={<RequireOrgMember><MemberServiceProgramsPage /></RequireOrgMember>} />
<Route path="app/notifications" element={<RequireOrgMember><MemberNotificationsPage /></RequireOrgMember>} />
<Route path="app/care" element={<RequireOrgMember><MemberCarePage /></RequireOrgMember>} />
<Route path="app/meetings" element={<RequireOrgMember><MemberMeetingsPage /></RequireOrgMember>} />
<Route path="app/directory" element={<RequireOrgMember><MemberDirectoryPage /></RequireOrgMember>} />
<Route path="app/member-services" element={<RequireVerifiedAccount><MemberServicesManagementPage /></RequireVerifiedAccount>} />
<Route path="app/family-history" element={<RequireOrgMember><FamilyHistoryPage /></RequireOrgMember>} />
<Route path="app/registrations" element={<RequireVerifiedAccount><MemberEventRegistrationsPage /></RequireVerifiedAccount>} />
<Route path="app/pledges" element={<RequireVerifiedAccount><MemberPledgesPage /></RequireVerifiedAccount>} />
        <Route path="app/resources" element={<RequireOrgMember><LearningResourcesPage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId/members" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId/chat" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId/meetings" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId/resources" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="app/teams/:teamId/announcements" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
        <Route path="attendance" element={<AttendanceRouteGate />} />
        <Route path="care" element={<RequireOrgModule moduleId="care-center"><CareCenterPage /></RequireOrgModule>} />
        <Route path="children-checkin" element={<ChildrenCheckInRouteGate />} />
        <Route path="children-checkout" element={<RequireOrgModule moduleId="children-checkout"><ChildrenCheckoutPage /></RequireOrgModule>} />
        <Route path="checkin" element={<OrgCheckInPage />} />
        <Route path="kiosk" element={<OrgCheckInPage />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="*" element={<OrganizationNotFound />} />
      </Route>
    </Routes>
  );
}

function AppRouteResolver({ children }) {
  const location = useLocation();
  const useCustomDomainPublicRoutes = shouldUseCustomDomainPublicRoutes(
    getCurrentHostname(),
    location.pathname,
  );

  return useCustomDomainPublicRoutes ? <CustomDomainPublicRoutes /> : children;
}

// -------------------- Router --------------------
function App() {
  React.useEffect(() => { installPushAccountBoundary(); }, []);
  if (redirectWwwToPrimary()) return null;
  if (redirectLegacyRccgHost()) return null;

  return (
    <BrowserRouter future={{ v7_startTransition: true }}>
      <PublicOrganizationProvider><PermissionsProvider><AttendanceVerificationProvider>
        <ForegroundNotificationBanner />
        <IdentityLinkBootstrap />
        <NavigationWarmup />
        <RouteErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
        <AppRouteResolver>
        <Routes>
          <Route element={<PersistentAdminLayout />}>
            <Route
            path="/platform/o/:orgSlug/c/:campaignId"
            element={<RequireCampaignEditAccess><CampaignAdminPage /></RequireCampaignEditAccess>}
            />
            <Route
            path="/platform/o/:orgSlug/dashboard"
            element={<RequireOrgMember><OrgDashboard /></RequireOrgMember>}
            />
            <Route
            path="/platform/o/:orgSlug/daycare"
            element={<RequireOrgMember><DaycareOverviewPage /></RequireOrgMember>}
            />
            <Route
            path="/platform/o/:orgSlug/daycare/staff"
            element={<RequireOrgModule moduleId="roles-access"><DaycareStaffAssignmentsPage /></RequireOrgModule>}
            />
            <Route path="/platform/o/:orgSlug/daycare/enrollment" element={<RequireOrgMember><DaycareEnrollmentQueuePage /></RequireOrgMember>} />
            <Route path="/platform/o/:orgSlug/daycare/relationships" element={<RequireOrgMember><DaycareRelationshipsPage /></RequireOrgMember>} />
            <Route path="/platform/o/:orgSlug/daycare/tuition" element={<RequireOrgMember><DaycareTuitionPage /></RequireOrgMember>} />
            <Route path="/platform/o/:orgSlug/daycare/classrooms" element={<RequireOrgMember><DaycareClassroomsPage /></RequireOrgMember>} />
            <Route path="/platform/o/:orgSlug/daycare/attendance" element={<RequireOrgMember><DaycareAttendancePage /></RequireOrgMember>} />
            <Route path="/platform/o/:orgSlug/daycare/care" element={<RequireOrgMember><DaycareCarePage /></RequireOrgMember>} />
            <Route
            path="/platform/o/:orgSlug/profile"
            element={<RequireOrgModule moduleId="website-builder"><OrgProfilePage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/team"
            element={<RequireOrgModule moduleId="roles-access"><TeamRolesPage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/roles"
            element={<RequireOrgModule moduleId="roles-access"><TeamRolesPage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/automations"
            element={<RequireOrgModule moduleId="automations"><AutomationCatalogPage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/notifications"
            element={<RequireOrgModule moduleId="communications"><NotificationCenterPage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/attendance"
            element={<AttendanceRouteGate />}
            />
            <Route path="/platform/o/:orgSlug/children-teachers" element={<RequireOrgMember><ChildrenTeachersPage /></RequireOrgMember>} />
            <Route
            path="/platform/o/:orgSlug/assign-follow-ups"
            element={<RequireOrgModule moduleId="assign-followups"><AttendanceDashboardPage initialView="followups" /></RequireOrgModule>}
            />
            <Route path="/platform/o/:orgSlug/my-teams" element={<RequireOrgMember><MyTeamsPage /></RequireOrgMember>} />
            <Route
            path="/admin/:orgSlug/notifications"
            element={<RequireOrgModule moduleId="communications"><NotificationCenterPage /></RequireOrgModule>}
            />
            <Route
            path="/admin/:orgSlug/attendance"
            element={<RequireOrgCapability permission="canManageAttendance"><AttendanceDashboardPage /></RequireOrgCapability>}
            />
            <Route
            path="/admin/:orgSlug/assign-follow-ups"
            element={<RequireOrgModule moduleId="assign-followups"><AttendanceDashboardPage initialView="followups" /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/funding"
            element={<RequireOrgAdmin><FundingCenterPage /></RequireOrgAdmin>}
            />
            <Route path="/platform/o/:orgSlug/opportunity-tasks" element={<RequireAuth><OpportunityTasksPage /></RequireAuth>} />
            <Route
            path="/platform/o/:orgSlug/donors"
            element={<RequireOrgModule moduleId="donors"><DonorsPage /></RequireOrgModule>}
            />
            <Route
            path="/platform/o/:orgSlug/reports"
            element={<RequireOrgModule moduleId="reports"><ReportsPage /></RequireOrgModule>}
            />
            <Route
            path="/admin/:orgSlug/funding"
            element={<RequireOrgAdmin><FundingCenterPage /></RequireOrgAdmin>}
            />
            <Route
            path="/admin/:orgSlug/campaigns"
            element={<RequireOrgCapability permission="canViewDashboard"><OrgDashboard /></RequireOrgCapability>}
            />
            <Route
            path="/admin/:orgSlug/campaigns/:campaignId/edit"
            element={<RequireCampaignEditAccess><CampaignAdminPage /></RequireCampaignEditAccess>}
            />
          </Route>

          {/* Lightly platform root. Never load a tenant here. */}
          <Route path="/" element={<LightlyPlatformHome />} />
          <Route path="/platform/website-preview/:organizationId" element={<WebsitePreviewPage />} />
          {/* Lightly Events. Creation is open before sign-in so the intent survives login. */}
          <Route path="/platform/events" element={<RequireAuth><EventsHomePage /></RequireAuth>} />
          <Route path="/platform/events/new" element={<EventEditorPage />} />
          <Route path="/platform/events/reports" element={<RequireAuth><EventsOperatorPage /></RequireAuth>} />
          <Route path="/platform/events/:eventId" element={<RequireAuth><EventManagePage /></RequireAuth>} />
          <Route path="/platform/events/:eventId/edit" element={<EventEditorPage />} />
          <Route path="/platform/events/:eventId/check-in" element={<RequireAuth><EventCheckInPage /></RequireAuth>} />
          <Route path="/platform/e/:eventId" element={<EventPublicPage />} />
          <Route path="/faith-sanctuary" element={<CanonicalPublicRouteRedirect />} />
          <Route path="/faith-sanctuary/become-a-fundraiser" element={<CanonicalPublicRouteRedirect />} />
          <Route path="/faith-sanctuary/f/:slug" element={<CanonicalPublicRouteRedirect />} />
          <Route path="/faith-sanctuary/f/:slug/donate" element={<CanonicalPublicRouteRedirect />} />
          <Route path="/faith-sanctuary/fundraisers/:fundraiserSlug/launch" element={<CanonicalPublicRouteRedirect />} />

          {/* ✅ Lightly public landing page */}
          <Route path="/lightly" element={<LightlyPlatformHome />} />

          <Route path="/children-invitation" element={<ChildrenInvitationPage />} />
          <Route path="/member-invitation" element={<MemberInvitationPage />} />
      <Route path="/auth/action" element={<AuthActionHandler />} />
          <Route path="/verify-email" element={<LegacyAuthRedirect />} />
          <Route path="/reset-password" element={<LegacyAuthRedirect />} />
          <Route path="/action" element={<LegacyAuthRedirect />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-phone" element={<PhoneRegistrationPage />} />
      <Route path="/connect-profile" element={<PhoneRegistrationPage />} />
          <Route path="/register" element={<Navigate to="/platform/onboarding/org" replace />} />

          {/* ✅ Organization signup (keep existing + add alias for navbar CTA) */}
          <Route path="/platform/signup" element={<Navigate to="/platform/onboarding/org" replace />} />
          <Route path="/signup-org" element={<Navigate to="/platform/onboarding/org" replace />} />

          {/* ✅ Unified Onboarding (UPDATED: HTTP search only) */}
          <Route path="/platform/onboarding" element={<OrgOnboardingWizard />} />
          <Route
            path="/platform/onboarding/unified"
            element={<OrgOnboardingWizard />}
          />
          {/* ✅ Org Onboarding Wizard (CONNECTED FLOW) */}
          <Route
            path="/platform/onboarding/org"
            element={<OrgOnboardingWizard />}
          />
          <Route
            path="/missions"
            element={<RequireAuth><IndividualMissionDiscoveryPage /></RequireAuth>}
          />
          <Route
            path="/discover/orgs"
            element={<IndividualMissionDiscoveryPage />}
          />
          <Route
            path="/discover/campaigns"
            element={<IndividualMissionDiscoveryPage />}
          />
          <Route
            path="/platform/o/:orgSlug/launch"
            element={<RequireOrgAdmin><OrganizationLaunchExperiencePage /></RequireOrgAdmin>}
          />
          <Route
            path="/platform/orgs/:orgSlug/launch"
            element={<RequireOrgAdmin><OrganizationLaunchExperiencePage /></RequireOrgAdmin>}
          />

          {/* Public participant page */}
          <Route path="/participant/:slug" element={<ParticipantPage />} />
          <Route path="/participant/:slug/*" element={<ParticipantPage />} />

          {/* Admin campaign page */}

{/* Onboarding routes (kept exactly as is) */}
          <Route
            path="/onboarding/organization-choice"
            element={
              <OrganizationChoice
                onBack={() => window.history.back()}
                onNext={({ orgType }) => {
                  console.log("Selected orgType:", orgType);
                }}
              />
            }
          />

          <Route
            path="/onboarding/MissionHookStep1"
            element={<MissionHookStep1 />}
          />

          <Route
            path="/onboarding/organizationpath"
            element={<Organizationpath />}
          />

          <Route
            path="/onboarding/organizationpath2"
            element={<Organizationpath2 />}
          />

          <Route
            path="/onboarding/individualpath"
            element={<Individualpath />}
          />

          <Route
            path="/onboarding/individualpath2"
            element={<Individualpath2 />}
          />

          <Route
            path="/onboarding/individualpath3"
            element={<Individualpath3 />}
          />
          <Route path="/o/:orgSlug/about" element={<PublicChurchRoutePage pageType="about" />} />
          <Route path="/o/:orgSlug/new-here" element={<PublicChurchRoutePage pageType="new-here" />} />
          <Route path="/o/:orgSlug/beliefs" element={<PublicChurchRoutePage pageType="beliefs" />} />
          <Route path="/o/:orgSlug/media" element={<PublicChurchRoutePage pageType="media" />} />
          <Route path="/o/:orgSlug/media/watch-live" element={<PublicChurchRoutePage pageType="watch-live" />} />
          <Route path="/o/:orgSlug/media/sermons" element={<PublicChurchRoutePage pageType="sermons" />} />
          <Route path="/o/:orgSlug/sermons" element={<PublicChurchRoutePage pageType="sermons" />} />
          <Route path="/o/:orgSlug/media/testimonies" element={<PublicChurchRoutePage pageType="testimonies" />} />
          <Route path="/o/:orgSlug/updates" element={<PublicChurchRoutePage pageType="updates" />} />
          <Route path="/o/:orgSlug/flyers" element={<PublicChurchRoutePage pageType="flyers" />} />
          <Route path="/o/:orgSlug/messages" element={<PublicServiceContentLibrary />} />
          <Route path="/o/:orgSlug/messages/:contentId" element={<PublicServiceContentLibrary />} />
          <Route path="/o/:orgSlug/series/:seriesId" element={<PublicServiceContentLibrary />} />
          <Route path="/o/:orgSlug/events" element={<PublicChurchRoutePage pageType="events" />} />
          <Route path="/o/:orgSlug/events/:eventId" element={<PublicChurchRoutePage pageType="event-detail" />} />
          <Route path="/o/:orgSlug/services" element={<PublicServiceHub />} />
          <Route path="/o/:orgSlug/services/:serviceId" element={<PublicServiceHub />} />
          <Route path="/o/:orgSlug/donate" element={<DonatePage />} />
          <Route path="/o/:orgSlug/give" element={<PublicChurchRoutePage pageType="giving" />} />
          <Route path="/o/:orgSlug/give/success" element={<DonationStatusPage status="success" />} />
          <Route path="/o/:orgSlug/giving" element={<PublicChurchRoutePage pageType="giving" />} />
          <Route path="/o/:orgSlug/campaigns" element={<PublicChurchRoutePage pageType="campaigns" />} />
          <Route path="/o/:orgSlug/campaigns/:campaignId/launch" element={<CampaignLaunchPage />} />
          <Route path="/o/:orgSlug/campaigns/:campaignId/success" element={<DonationStatusPage status="success" />} />
          <Route path="/o/:orgSlug/campaigns/:campaignId" element={<PublicCampaignPage />} />
          <Route path="/o/:orgSlug/fundraisers/:fundraiserId/success" element={<DonationStatusPage status="success" />} />
          <Route path="/o/:orgSlug/contact" element={<PublicChurchRoutePage pageType="contact" />} />
          <Route path="/o/:orgSlug/prayer-request" element={<PublicChurchRoutePage pageType="prayer" />} />
          <Route path="/o/:orgSlug/privacy" element={<PublicChurchRoutePage pageType="privacy" />} />
          <Route path="/o/:orgSlug/refund-policy" element={<PublicChurchRoutePage pageType="refund-policy" />} />
          <Route path="/o/:orgSlug/terms" element={<PublicChurchRoutePage pageType="terms" />} />
          <Route path="/o/:orgSlug/ministries" element={<PublicMinistryPage />} />
          <Route path="/o/:orgSlug/ministries/:ministrySlug" element={<PublicMinistryPage />} />
          <Route path="/o/:orgSlug/gallery" element={<GalleryPage />} />
          <Route
            path="/o/:orgSlug/leadership"
            element={<PublicLeadershipPage />}
          />
          <Route
            path="/o/:orgSlug/about/leadership"
            element={<PublicLeadershipPage />}
          />
          <Route
            path="/platform/onboarding/slide-3"
            element={<OnboardingSlide3 />}
          />
          <Route
            path="/platform/onboarding/slide-4"
            element={<OnboardingSlide4 />}
          />
          <Route
            path="/platform/onboarding/slide-5"
            element={<OnboardingSlide5 />}
          />
          <Route
            path="/platform/onboarding/slide-6"
            element={<OnboardingSlide6 />}
          />
          <Route path="/o/:orgSlug/attendance" element={<AttendanceRouteGate />} />
          <Route path="/o/:orgSlug/kiosk" element={<OrgCheckInPage />} />
          <Route
            path="/platform/onboarding/slide-1"
            element={<OnboardingSlide1 />}
          />
          <Route
            path="/platform/onboarding/slide-2"
            element={<OnboardingSlide2 />}
          />

          {/* ✅ Public Organization Landing Page */}
          <Route path="/o/:orgSlug" element={<PublicOrgDashboardPage />} />
          <Route path="/o/:orgSlug/daycare" element={<PublicDaycarePage />} />
          <Route path="/o/:orgSlug/menu" element={<CuisineMenuPage />} />
          <Route path="/o/:orgSlug/cuisine" element={<PublicCuisineStorefrontPage />} />
          <Route path="/o/:orgSlug/catering/request" element={<CuisineCateringRequestPage />} />
          <Route path="/o/:orgSlug/catering/quote/:quoteId" element={<CuisineQuotePage />} />

          {/* ✅ SAAS UPDATED: Organization-specific Groups Page */}
          <Route path="/o/:orgSlug/groups" element={<GroupsPage />} />

          {/* ✅ LEGACY FALLBACK: Old groups path still points to RCCG context */}
          <Route path="/groups" element={<GroupsPage />} />

          {/* ✅ Add this to handle organization-level donations */}
          <Route path="/org-donate/:orgSlug" element={<DonatePage />} />

          {/* Zeffy-style donate flow (kept exactly as is) */}
          <Route path="/donate/:slug" element={<LegacyDonationRouteGate />} />
          <Route path="/donate" element={<ChooseOrganizationForDonation />} />

          {/* Platform landing */}
          <Route path="/platform" element={<Navigate to="/" replace />} />
          <Route
            path="/platform/:orgSlug/dashboard"
            element={<LegacyPlatformDashboardRedirect />}
          />
          <Route
            path="/platform/o/:orgSlug"
            element={<RequireAuth><OrganizationManagementEntry /></RequireAuth>}
          />

          <Route
            path="/platform/o/:orgSlug/campaigns/new"
            element={<RequireOrgCapability permission={["canManageCampaigns", "canManageFundraising", "canApproveCampaigns"]}><CampaignCreatePageNew /></RequireOrgCapability>}
          />
          <Route
            path="/campaigns/proposals/:proposalId"
            element={<CampaignProposalPage />}
          />
          <Route
            path="/admin/campaign-proposals"
            element={<CampaignProposalReviewPage />}
          />
          <Route
            path="/admin/campaign-proposals/:proposalId"
            element={<CampaignProposalReviewPage />}
          />

          <Route
            path="/o/:orgSlug/c/:campaignId/become-a-fundraiser"
            element={<CampaignParticipantSignupPage />}
          />
          <Route
            path="/o/:orgSlug/campaigns/:campaignId/f/:slug/edit"
            element={<CampaignParticipantSignupPage />}
          />
          <Route
            path="/o/:orgSlug/campaigns/:campaignId/fundraisers/:slug/edit"
            element={<CampaignParticipantSignupPage />}
          />
          <Route
            path="/o/:orgSlug/c/:campaignId/fundraisers/:fundraiserSlug/launch"
            element={<FundraiserLaunchPage />}
          />

          <Route path="/o/:orgSlug/profile" element={<OrgPublicProfile />} />

          <Route
            path="/o/:orgSlug/c/:campaignId"
            element={<PublicCampaignPage />}
          />
          <Route
            path="/o/:orgSlug/c/:campaignId/success"
            element={<DonationStatusPage status="success" />}
          />

          <Route
            path="/o/:orgSlug/c/:campaignId/donate"
            element={<DonatePage />}
          />

          <Route
            path="/platform/onboarding/start"
            element={<OnboardingSlide1 />}
          />
          <Route path="/o/:orgSlug/checkin" element={<OrgCheckInPage />} />
          <Route path="/o/:orgSlug/bible" element={<OrgBiblePage />} />
          <Route path="/o/:orgSlug/me" element={<RequireVerifiedAccount><MemberProfilePage /></RequireVerifiedAccount>} />
          <Route path="/o/:orgSlug/my-giving" element={<RequireVerifiedAccount><MyGivingPage /></RequireVerifiedAccount>} />
<Route path="/o/:orgSlug/settings" element={<RequireVerifiedAccount><MemberSettingsPage /></RequireVerifiedAccount>} />
          <Route path="/o/:orgSlug/app/teams" element={<RequireOrgMember><MyTeamsPage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/service-programs" element={<RequireOrgMember><MemberServiceProgramsPage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/notifications" element={<RequireOrgMember><MemberNotificationsPage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/care" element={<RequireOrgMember><MemberCarePage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/meetings" element={<RequireOrgMember><MemberMeetingsPage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/directory" element={<RequireOrgMember><MemberDirectoryPage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/member-services" element={<RequireVerifiedAccount><MemberServicesManagementPage /></RequireVerifiedAccount>} />
<Route path="/o/:orgSlug/app/family-history" element={<RequireOrgMember><FamilyHistoryPage /></RequireOrgMember>} />
<Route path="/o/:orgSlug/app/registrations" element={<RequireVerifiedAccount><MemberEventRegistrationsPage /></RequireVerifiedAccount>} />
<Route path="/o/:orgSlug/app/pledges" element={<RequireVerifiedAccount><MemberPledgesPage /></RequireVerifiedAccount>} />
          <Route path="/o/:orgSlug/app/resources" element={<RequireOrgMember><LearningResourcesPage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/announcements" element={<RequireOrgMember><AnnouncementsInboxPage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/updates/:announcementId" element={<PublicAnnouncementSharePage />} />
          <Route path="/o/:orgSlug/app/teams/:teamId" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/teams/:teamId/members" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/teams/:teamId/chat" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/teams/:teamId/meetings" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/teams/:teamId/resources" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/app/teams/:teamId/announcements" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams" element={<RequireOrgMember><MyTeamsPage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/service-programs" element={<RequireOrgMember><MemberServiceProgramsPage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/notifications" element={<RequireOrgMember><MemberNotificationsPage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/care" element={<RequireOrgMember><MemberCarePage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/meetings" element={<RequireOrgMember><MemberMeetingsPage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/directory" element={<RequireOrgMember><MemberDirectoryPage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/member-services" element={<RequireVerifiedAccount><MemberServicesManagementPage /></RequireVerifiedAccount>} />
<Route path="/:orgSlug/app/family-history" element={<RequireOrgMember><FamilyHistoryPage /></RequireOrgMember>} />
<Route path="/:orgSlug/app/registrations" element={<RequireVerifiedAccount><MemberEventRegistrationsPage /></RequireVerifiedAccount>} />
<Route path="/:orgSlug/app/pledges" element={<RequireVerifiedAccount><MemberPledgesPage /></RequireVerifiedAccount>} />
          <Route path="/:orgSlug/app/resources" element={<RequireOrgMember><LearningResourcesPage /></RequireOrgMember>} />
          <Route path="/:orgSlug/announcements" element={<RequireOrgMember><AnnouncementsInboxPage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId/members" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId/chat" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId/meetings" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId/resources" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/:orgSlug/app/teams/:teamId/announcements" element={<RequireOrgMember><OrganizationTeamWorkspacePage /></RequireOrgMember>} />
          <Route path="/o/:orgSlug/children-checkin" element={<ChildrenCheckInRouteGate />} />
          <Route path="/o/:orgSlug/children-checkout" element={<RequireOrgModule moduleId="children-checkout"><ChildrenCheckoutPage /></RequireOrgModule>} />
          <Route path="/:orgSlug/donors" element={<RequireOrgModule moduleId="donors"><DonorsPage /></RequireOrgModule>} />
          <Route path="/:orgSlug/reports" element={<RequireOrgModule moduleId="reports"><ReportsPage /></RequireOrgModule>} />
          <Route path="/:orgSlug/care" element={<RequireOrgModule moduleId="care-center"><CareCenterPage /></RequireOrgModule>} />
          <Route path="/:orgSlug/groups/manage" element={<RequireOrgModule moduleId="groups-programs"><OrgGroupsManagerPage /></RequireOrgModule>} />
          <Route path="/:shortSlug/churchservice/checkin" element={<OrgCheckInPage />} />
          <Route
            path="/onboarding/slide-2"
            element={
              <OnboardingSlide2
                prevData={{ country: "US" }}
                onNext={(data) => console.log("NEXT DATA:", data)}
                onBack={() => console.log("BACK")}
              />
            }
          />

          <Route path="/test/onboarding" element={<OnboardingTestLab />} />

<Route path="/platform/o/:orgSlug/daycare/enrollment/apply" element={<RequireAuth><DaycareEnrollmentApplyPage /></RequireAuth>} />

<Route path="/platform/o/:orgSlug/daycare/parent" element={<RequireAuth><DaycareParentPortalPage /></RequireAuth>} />

<Route path="/platform/o/:orgSlug/cuisine" element={<RequireOrgCapability permission={["canManageCuisine", "canEditWebsite"]}><RequireCuisineOrganization><CuisineStudioPage /></RequireCuisineOrganization></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/cuisine/catalog" element={<RequireOrgCapability permission={["canManageCuisine", "canEditWebsite"]}><RequireCuisineOrganization><CuisineCatalogEditorPage /></RequireCuisineOrganization></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/cuisine/requests" element={<RequireOrgCapability permission="canManageCuisine"><RequireCuisineOrganization><CuisineRequestInboxPage /></RequireCuisineOrganization></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/cuisine/policies" element={<RequireOrgCapability permission="canManageCuisine"><RequireCuisineOrganization><CuisinePolicyPage /></RequireCuisineOrganization></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/cuisine/bookings" element={<RequireOrgCapability permission="canManageCuisine"><RequireCuisineOrganization><CuisineBookingDiaryPage /></RequireCuisineOrganization></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/daycare/pickup" element={<RequireOrgMember><ChildrenCheckoutPage /></RequireOrgMember>} />
          <Route
            path="/platform/o/:orgSlug/campaigns/:campaignId/fundraisers/:slug/preview"
            element={<RequireOrgCapability permission={["canApproveCampaigns", "canManageCampaigns", "canManageGiving", "canViewDashboard"]}><ParticipantPage /></RequireOrgCapability>}
          />

          <Route
            path="/platform/o/:orgSlug/verify"
            element={<RequireOrgAdmin><OrgVerificationPage /></RequireOrgAdmin>}
          />
          <Route
            path="/platform/o/:orgSlug/payouts"
            element={<RequireOrgModule moduleId="stripe-payouts"><OrgPayoutSetupPage /></RequireOrgModule>}
          />

<Route
            path="/platform/o/:orgSlug/children-checkin"
            element={<ChildrenCheckInRouteGate />}
          />
          <Route
            path="/platform/o/:orgSlug/children-classes"
            element={<RequireOrgModule moduleId="children-classes"><ChildrenClassesAdminPage /></RequireOrgModule>}
          />

          <Route
            path="/platform/o/:orgSlug/children-checkout"
            element={<RequireOrgModule moduleId="children-checkout"><ChildrenCheckoutPage /></RequireOrgModule>}
          />

          <Route
            path="/platform/o/:orgSlug/follow-ups"
            element={<RequireOrgMember><MyFollowUpsPage /></RequireOrgMember>}
          />
          <Route
            path="/platform/o/:orgSlug/announcements"
            element={<RequireOrgModule moduleId="announcements"><PlatformOrgRedirect to="/notifications" /></RequireOrgModule>}
          />
          <Route path="/platform/o/:orgSlug/team-workspaces/:teamId/*" element={<RequireOrgCapability permission="canManageTeams"><OrganizationTeamWorkspacePage /></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/teams" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />

          <Route path="/platform/o/:orgSlug/teams/new" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/teams/:teamId" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/teams/:teamId/members" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/platform/o/:orgSlug/teams/:teamId/settings" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />

          <Route
            path="/admin/:orgSlug/announcements"
            element={<RequireOrgModule moduleId="announcements"><PlatformOrgRedirect to="/notifications" /></RequireOrgModule>}
          />
          <Route path="/admin/:orgSlug/teams" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/admin/:orgSlug/teams/new" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/admin/:orgSlug/teams/:teamId" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/admin/:orgSlug/teams/:teamId/members" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
          <Route path="/admin/:orgSlug/teams/:teamId/settings" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />

          <Route
            path="/admin/:orgSlug/children-checkin"
            element={<ChildrenCheckInRouteGate />}
          />

          <Route
            path="/admin/:orgSlug/follow-ups"
            element={<RequireOrgMember><MyFollowUpsPage /></RequireOrgMember>}
          />
          <Route
            path="/platform/labs/adaptive-experience"
            element={<RequireAuth><AdaptiveExperienceLabPage /></RequireAuth>}
          />
          <Route
            path="/platform/o/:orgSlug/adaptive-lab"
            element={<RequireOrgAdmin><AdaptiveExperienceLabPage /></RequireOrgAdmin>}
          />

          <Route
            path="/dashboard/:orgSlug/funding"
            element={<RequireOrgAdmin><FundingCenterPage /></RequireOrgAdmin>}
          />

<Route
            path="/platform/o/:orgSlug/care"
            element={<RequireOrgModule moduleId="care-center"><CareCenterPage /></RequireOrgModule>}
          />
          <Route
            path="/admin/:orgSlug/donors"
            element={<PrivateOrgRedirect to="/donors" />}
          />
          <Route
            path="/admin/:orgSlug/reports"
            element={<PrivateOrgRedirect to="/reports" />}
          />
          <Route
            path="/admin/:orgSlug/care"
            element={<PrivateOrgRedirect to="/care" />}
          />

          <Route
            path="/admin/:orgSlug/visitors"
            element={<RequireOrgModule moduleId="website-visitors"><WebsiteVisitorsPage /></RequireOrgModule>}
          />
          <Route
            path="/admin/:orgSlug/media-vault"
            element={<RequireOrgModule moduleId="media-vault"><MediaVaultPage /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/media-vault"
            element={<RequireOrgModule moduleId="media-vault"><MediaVaultPage /></RequireOrgModule>}
          />

<Route
            path="/:orgSlug/admin/visitors"
            element={<RequireOrgModule moduleId="website-visitors"><WebsiteVisitorsPage /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/design"
            element={<RequireOrgModule moduleId="website-builder"><DesignCenterPage /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/services"
            element={<RequireOrgModule moduleId="services"><ServiceManager /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/groups"
            element={<RequireOrgAccess require="canManageEvents"><PlatformOrgRedirect to="/groups/manage" /></RequireOrgAccess>}
          />
          <Route
            path="/platform/o/:orgSlug/groups/manage"
            element={<RequireOrgModule moduleId="groups-programs"><OrgGroupsManagerPage /></RequireOrgModule>}
          />
          <Route
            path="/admin/:orgSlug/groups"
            element={<RequireOrgAccess require="canManageEvents"><PlatformOrgRedirect to="/groups/manage" /></RequireOrgAccess>}
          />
          <Route
            path="/admin/:orgSlug/groups/manage"
            element={<RequireOrgModule moduleId="groups-programs"><OrgGroupsManagerPage /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/service-content"
            element={<RequireOrgModule moduleId="sermons-messages"><ServiceContentAdminPage /></RequireOrgModule>}
          />
          <Route
            path="/platform/o/:orgSlug/*"
            element={<RequireAuth><PlatformOrgRedirect to="/dashboard" /></RequireAuth>}
          />

          {/* 🔎 DATABASE SEARCH */}
          <Route
            path="/test-search"
            element={
              <div style={{ padding: 40, maxWidth: 800, margin: "0 auto" }}>
                <h2>Organization Database Search</h2>
                <OrganizationSearchBox
                  onSelect={(org) => {
                    console.log("SELECTED ORG:", org);
                    alert(`Selected: ${org.org_name}`);
                  }}
                />
              </div>
            }
          />

          <Route path="/dashboard/:orgSlug" element={<RequireAuth><PlatformOrgRedirect to="/dashboard" /></RequireAuth>} />
          <Route path="/dashboard/:orgSlug/*" element={<RequireAuth><PlatformOrgRedirect to="/dashboard" /></RequireAuth>} />
          <Route path="/admin/:orgSlug" element={<RequireAuth><PlatformOrgRedirect to="/dashboard" /></RequireAuth>} />
          <Route path="/admin/:orgSlug/*" element={<RequireAuth><PlatformOrgRedirect to="/dashboard" /></RequireAuth>} />
          <Route path="/platform/admin/tax-review" element={<PlatformTaxReviewPage />} />
          <Route path="/platform/admin/governance" element={<PlatformGovernancePage />} />
          <Route path="/dashboard/*" element={<RequireAuth><PlatformHome /></RequireAuth>} />
          <Route path="/admin/*" element={<RequireAuth><AdminDashboard /></RequireAuth>} />

          <Route
            path="/signup-participant"
            element={<ParticipantSignupPage />}
          />
          <Route
            path="/become-a-fundraiser"
            element={<ParticipantSignupPage />}
          />
          {/* On platform hosts, member identity must always carry an explicit tenant. */}
          <Route path="/me" element={<PlatformMemberContextPage />} />
          <Route
            path="/:legacyParentSlug/o/:orgSlug"
            element={<LegacyNestedOrganizationRedirect />}
          />
          <Route
            path="/:legacyParentSlug/o/:orgSlug/*"
            element={<LegacyNestedOrganizationRedirect />}
          />
      <Route path="/:orgSlug" element={<PublicOrganizationWebsite />}>
        <Route index element={<PublicOrgDashboardPage />} />
        <Route path="donate" element={<DonatePage />} />
        <Route path="success" element={<DonationStatusPage status="success" />} />
        <Route path="cancel" element={<DonationStatusPage status="cancel" />} />
        <Route path="about" element={<PublicChurchRoutePage pageType="about" />} />
            <Route path="new-here" element={<PublicChurchRoutePage pageType="new-here" />} />
            <Route path="beliefs" element={<PublicChurchRoutePage pageType="beliefs" />} />
            <Route path="media" element={<PublicChurchRoutePage pageType="media" />} />
            <Route path="media/watch-live" element={<PublicChurchRoutePage pageType="watch-live" />} />
            <Route path="media/sermons" element={<PublicChurchRoutePage pageType="sermons" />} />
            <Route path="media/testimonies" element={<PublicChurchRoutePage pageType="testimonies" />} />
            <Route path="updates" element={<PublicChurchRoutePage pageType="updates" />} />
        <Route path="updates/:announcementId" element={<PublicAnnouncementSharePage />} />
            <Route path="flyers" element={<PublicChurchRoutePage pageType="flyers" />} />
            <Route path="messages" element={<PublicServiceContentLibrary />} />
            <Route path="messages/:contentId" element={<PublicServiceContentLibrary />} />
            <Route path="series/:seriesId" element={<PublicServiceContentLibrary />} />
            <Route path="events" element={<PublicChurchRoutePage pageType="events" />} />
            <Route path="events/:eventId" element={<PublicChurchRoutePage pageType="event-detail" />} />
            <Route path="services" element={<PublicServiceHub />} />
            <Route path="services/:serviceId" element={<PublicServiceHub />} />
            <Route path="menu" element={<PublicChurchRoutePage pageType="menu" />} />
            <Route path="give" element={<PublicChurchRoutePage pageType="giving" />} />
            <Route path="give/success" element={<DonationStatusPage status="success" />} />
            <Route path="giving" element={<PublicChurchRoutePage pageType="giving" />} />
            <Route path="campaigns" element={<PublicChurchRoutePage pageType="campaigns" />} />
            <Route path="campaigns/:campaignId/edit" element={<RequireCampaignEditAccess><CampaignAdminPage /></RequireCampaignEditAccess>} />
            <Route path="campaigns/:campaignId/launch" element={<CampaignLaunchPage />} />
            <Route path="campaigns/:campaignId/success" element={<DonationStatusPage status="success" />} />
            <Route path="campaigns/:campaignId" element={<PublicCampaignPage />} />
            <Route path="campaigns/:campaignId/donate" element={<DonatePage />} />
            <Route path="campaigns/:campaignId/become-a-fundraiser" element={<CampaignParticipantSignupPage />} />
            <Route path="campaigns/:campaignId/fundraisers/:slug" element={<ParticipantPage />} />
            <Route path="campaigns/:campaignId/fundraisers/:slug/edit" element={<CampaignParticipantSignupPage />} />
            <Route path="campaigns/:campaignId/fundraisers/:slug/donate" element={<DonatePage />} />
            <Route path="campaigns/:campaignId/f/:slug" element={<ParticipantPage />} />
            <Route path="campaigns/:campaignId/f/:slug/edit" element={<CampaignParticipantSignupPage />} />
            <Route path="campaigns/:campaignId/f/:slug/donate" element={<DonatePage />} />
            <Route path="fundraisers/:fundraiserId/success" element={<DonationStatusPage status="success" />} />
            <Route path="contact" element={<PublicChurchRoutePage pageType="contact" />} />
            <Route path="prayer-request" element={<PublicChurchRoutePage pageType="prayer" />} />
            <Route path="privacy" element={<PublicChurchRoutePage pageType="privacy" />} />
            <Route path="refund-policy" element={<PublicChurchRoutePage pageType="refund-policy" />} />
            <Route path="terms" element={<PublicChurchRoutePage pageType="terms" />} />
            <Route path="ministries" element={<PublicMinistryPage />} />
            <Route path="ministries/:ministrySlug" element={<PublicMinistryPage />} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="leadership" element={<PublicLeadershipPage />} />
            <Route path="about/leadership" element={<PublicLeadershipPage />} />
            <Route path="more" element={<OrgMorePage />} />
            <Route path="bible" element={<OrgBiblePage />} />
            <Route path="me" element={<RequireAuth><MemberProfilePage /></RequireAuth>} />
            <Route path="app/teams" element={<RequireAuth><MyTeamsPage /></RequireAuth>} />
            <Route path="app/service-programs" element={<RequireOrgMember><MemberServiceProgramsPage /></RequireOrgMember>} />
<Route path="app/notifications" element={<RequireOrgMember><MemberNotificationsPage /></RequireOrgMember>} />
<Route path="app/care" element={<RequireOrgMember><MemberCarePage /></RequireOrgMember>} />
<Route path="app/meetings" element={<RequireOrgMember><MemberMeetingsPage /></RequireOrgMember>} />
<Route path="app/directory" element={<RequireOrgMember><MemberDirectoryPage /></RequireOrgMember>} />
<Route path="app/member-services" element={<RequireVerifiedAccount><MemberServicesManagementPage /></RequireVerifiedAccount>} />
<Route path="app/family-history" element={<RequireOrgMember><FamilyHistoryPage /></RequireOrgMember>} />
<Route path="app/registrations" element={<RequireVerifiedAccount><MemberEventRegistrationsPage /></RequireVerifiedAccount>} />
<Route path="app/pledges" element={<RequireVerifiedAccount><MemberPledgesPage /></RequireVerifiedAccount>} />
        <Route path="app/resources" element={<RequireAuth><LearningResourcesPage /></RequireAuth>} />
            <Route path="announcements" element={<RequireAuth><AnnouncementsInboxPage /></RequireAuth>} />
            <Route path="app/teams/:teamId" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="app/teams/:teamId/members" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="app/teams/:teamId/chat" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="app/teams/:teamId/meetings" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="app/teams/:teamId/resources" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="app/teams/:teamId/announcements" element={<RequireAuth><OrganizationTeamWorkspacePage /></RequireAuth>} />
            <Route path="profile" element={<OrgPublicProfile />} />
            <Route path="participant/:slug" element={<ParticipantPage />} />
            <Route path="participant/:slug/*" element={<ParticipantPage />} />
            <Route path="groups" element={<GroupsPage />} />
            <Route path="c/:campaignId" element={<CanonicalPublicRouteRedirect />} />
            <Route path="c/:campaignId/success" element={<DonationStatusPage status="success" />} />
            <Route path="c/:campaignId/donate" element={<CanonicalPublicRouteRedirect />} />
            <Route path="c/:campaignId/become-a-fundraiser" element={<CanonicalPublicRouteRedirect />} />
            <Route path="c/:campaignId/fundraisers/:fundraiserSlug/launch" element={<FundraiserLaunchPage />} />
            <Route path="donate/:donationRouteSlug" element={<DonatePage />} />
            <Route path="checkin" element={<OrgCheckInPage />} />
            <Route path="attendance" element={<AttendanceRouteGate />} />
            <Route path="care" element={<RequireOrgModule moduleId="care-center"><CareCenterPage /></RequireOrgModule>} />
            <Route path="kiosk" element={<OrgCheckInPage />} />
            <Route path="my-giving" element={<RequireAuth><MyGivingPage /></RequireAuth>} />
<Route path="settings" element={<RequireVerifiedAccount><MemberSettingsPage /></RequireVerifiedAccount>} />
            <Route path="children-checkin" element={<ChildrenCheckInRouteGate />} />
            <Route path="children-checkout" element={<RequireOrgModule moduleId="children-checkout"><ChildrenCheckoutPage /></RequireOrgModule>} />
            <Route path="admin/design" element={<RequireAuth><DesignCenterPage /></RequireAuth>} />
            <Route path="admin/funding" element={<RequireAuth><FundingCenterPage /></RequireAuth>} />
            <Route path="admin/teams" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
            <Route path="admin/teams/new" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
            <Route path="admin/teams/:teamId" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
            <Route path="admin/teams/:teamId/members" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
            <Route path="admin/teams/:teamId/settings" element={<RequireOrgCapability permission="canManageTeams"><AdminOrganizationTeamsPage /></RequireOrgCapability>} />
            <Route path="*" element={<OrganizationNotFound />} />
          </Route>
        </Routes>
        </AppRouteResolver>
        </Suspense>
        </RouteErrorBoundary>
      </AttendanceVerificationProvider></PermissionsProvider></PublicOrganizationProvider>
      </BrowserRouter>
  );
}

export default App;
